import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ParcelTemplatesService from "../../../services/shipment-service/parcelTemplates.ts";
import {
  convertParcelTemplateToCreate,
  convertParcelTemplateToUpdate,
  normalize,
  ParcelTemplate,
  validate,
} from "../../../Models/ParcelTemplate.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import { useAppContext } from "../../../AppProvider";
import {
  callErrorToast,
  callSuccessToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities.ts";

const parcelTemplatesService = new ParcelTemplatesService();

interface HandlerParcelTemplateProviderProps {
  id?: string;
  children: ReactNode;
  callback?: () => void;
  autosave: boolean;
}

interface HandlerParcelTemplateContextValue {
  data: Partial<ParcelTemplate>;
  error: ErrorResponse<ParcelTemplate> | null;
  loader: boolean;
  callback?: () => void;
  edit: (
    parcelTemplate: ParcelTemplate,
    property: keyof ParcelTemplate | null,
    save: boolean
  ) => void;
  onSubmit: (id?: string) => void;
}

const HandlerParcelTemplateContext = createContext<
  HandlerParcelTemplateContextValue | undefined
>(undefined);

const HandlerParcelTemplateProvider: React.FC<
  HandlerParcelTemplateProviderProps
> = ({ id, children, autosave = false, callback }) => {
  const { dictionary } = useAppContext();
  const [data, setData] = useState<Partial<ParcelTemplate>>({});
  const [loader, setLoader] = useState<boolean>(true);
  const [error, setError] = useState<ErrorResponse<ParcelTemplate> | null>(
    null
  );
  const prevError = useRef<ErrorResponse<ParcelTemplate>>(null);

  const getParcelTemplate = (id: string): void => {
    setLoader(true);
    parcelTemplatesService
      .get(id)
      .then((res) => {
        setData(res.data);
        setLoader(false);
      })
      .catch((err) => {
        callErrorToast(err);
        setLoader(false);
        setError(err);
      });
  };

  const create = (parcelTemplate: ParcelTemplate): void => {
    const validation = validate(data as ParcelTemplate);
    if (!validation.isValid) {
      setError({ response: { data: validation.errors } });
      setLoader(false);
      return;
    }
    const parcelTemplateToCreate =
      convertParcelTemplateToCreate(parcelTemplate);

    parcelTemplatesService
      .create(parcelTemplateToCreate)
      .then((res) => {
        setData(res.data);
        setLoader(false);
        if (callback) {
          callback();
        }
        callSuccessToast(dictionary.messages.save_successful);
      })
      .catch((err) => {
        setError(err);
        callErrorToast(err);
        setLoader(false);
      });
  };

  const edit = (
    parcelTemplate: ParcelTemplate,
    property: keyof ParcelTemplate | null,
    save = true
  ): void => {
    parcelTemplate = normalize(parcelTemplate);
    parcelTemplate = cleanObject(parcelTemplate);
    if (property) {
      removeError(property, error, prevError, setError);
    }
    if (save && autosave) {
      const validation = validate(data as ParcelTemplate);
      if (!validation.isValid) {
        setError({ response: { data: validation.errors } });
        setLoader(false);
        return;
      }

      const parcelTemplateUpdated =
        convertParcelTemplateToUpdate(parcelTemplate);

      parcelTemplatesService
        .edit(parcelTemplateUpdated)
        .then(() => {
          setData(parcelTemplate);
          setLoader(false);
          if (save && callback) {
            callback();
          }
          callSuccessToast(dictionary.messages.save_successful);
        })
        .catch((err) => {
          setError(err);
          callErrorToast(err);
          setLoader(false);
        });
      return;
    }
    setData(parcelTemplate);
  };

  const onSubmit = (id?: string): void => {
    setLoader(true);
    if (id) {
      edit(data as ParcelTemplate, null);
      return;
    }
    create(data as ParcelTemplate);
  };

  useEffect(() => {
    if (id) {
      getParcelTemplate(id);
      return;
    }
    setLoader(false);
  }, []);

  return (
    <HandlerParcelTemplateContext.Provider
      value={{
        data,
        error,
        loader,
        edit,
        onSubmit,
        callback,
      }}
    >
      {children}
    </HandlerParcelTemplateContext.Provider>
  );
};

const useHandlerContext = (): HandlerParcelTemplateContextValue => {
  const context = useContext(HandlerParcelTemplateContext);
  if (!context) {
    throw new Error(
      "useHandlerContext must be used within a HandlerParcelTemplateProvider"
    );
  }
  return context;
};

export { HandlerParcelTemplateProvider, useHandlerContext };
